'use client';

import { useState, useEffect, useCallback } from 'react';

import axiosInstance, { endpoints } from 'src/lib/axios';

// ----------------------------------------------------------------------

export type MenyInställning = { nyckel: string; synlig: boolean; roller?: string[] };

type MenyInställningarSvar = { inställningar: MenyInställning[] };

/**
 * Bygger en synlighetskarta (nyckel → synlig) som bakåtkompatibilitet.
 */
export function tillSynlighetskarta(
  inställningar: MenyInställning[]
): Record<string, boolean> {
  return inställningar.reduce<Record<string, boolean>>((acc, i) => {
    acc[i.nyckel] = i.synlig;
    return acc;
  }, {});
}

/**
 * Bygger en samlad inställningskarta (nyckel → { synlig, roller }) som `filtreraNavigering`
 * använder för både synlighets- och rollkontroll.
 */
export function tillInställningskarta(
  inställningar: MenyInställning[]
): Record<string, MenyInställning> {
  return inställningar.reduce<Record<string, MenyInställning>>((acc, i) => {
    acc[i.nyckel] = i;
    return acc;
  }, {});
}

/**
 * Hämtar en organisations menyinställningar. Fel sväljs medvetet – navigeringen
 * ska falla tillbaka på "allt synligt" om anropet misslyckas.
 */
export function useMenyInställningar(orgId?: string) {
  const [inställningar, setInställningar] = useState<MenyInställning[]>([]);
  const [loading, setLoading] = useState(false);

  const hämta = useCallback(async () => {
    if (!orgId) {
      setInställningar([]);
      return;
    }
    setLoading(true);
    try {
      const res = await axiosInstance.get<MenyInställningarSvar>(
        endpoints.organisation.menyinställningar(orgId)
      );
      setInställningar(res.data?.inställningar ?? []);
    } catch (error) {
      console.error('Kunde inte hämta menyinställningar', error);
      setInställningar([]);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    hämta();
  }, [hämta]);

  return { inställningar, loading, uppdatera: hämta };
}
